import React, {Component} from "react";
import axios from "axios";
import {createRoot} from 'react-dom/client';
import {Box, Container, Typography} from "@mui/material";

import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs";
import SearchBar from "../components/SearchBar/SearchBar";
import Grid from "../components/Grid/Grid";

import ConfigurationHelper from "../configuration/ClientConfigurationHelper";
import ThemeProviderWrapper from "../hoc/ThemeProviderWrapper";

import "../../sass/_document.scss";
import "../../sass/home.scss";

const apiUrl = ConfigurationHelper.getContentSearchUrl();

class Home extends Component {
    state={
        loaded: true,
        category: "",
        sort: "most_recent"
    }

    updateRequestParameter = ({category, sort} ) => {
        this.setState({loaded: false});
        if(category){
            this.setState({category: category});
        }
        if(sort){
            this.setState({sort: sort});
        }
        setTimeout(()=>{
            this.setState({loaded: true});
        },300)
    };

    resetRequestParameter = () => {
        this.setState({loaded: false, category: '', sort: 'most_recent'});
        setTimeout(()=>{
            this.setState({loaded: true});
        },300)
    }

    fetchAssets = async ({ page, filters }) => {
        const { category, sort } = filters || {};
        let content = "";
        let contentFilters = `{attribute:{code: "show_on_grid" , value: "yes"}}`;
        let contentSort = "";

        if(category && category !== "All"){
            contentFilters = contentFilters +`,{attribute:{ code: "category", value:"${category}"}}`;
        }
        if(sort){
            const value = sort;
            if(value==="most_recent"){
                contentSort = `, sort:{name: creation_date, direction: desc}`;
            }
            if (value==="creator_name"){
                contentSort = `, sort:{name: last_name, direction: asc}`;
            }
            if(value==="with_video"){
                contentSort = `, sort:{name: content_type, direction: desc}`;
            }
            if (value === "views") {
                contentSort = `, sort:{name: engagement, direction: desc}`;
            }
        }

        if(contentFilters){
            content = `, filter: {
                AND:[
                    ${contentFilters}
                ]
            }`
        }

        const requestQuery = `{
            content(page: ${page}, per_page: 12, ${content} ${contentSort}) {
                total
                items {
                    content_id
                    title
                    page_url
                    description
                    creator {
                        creator_id
                        employee_id
                        uri_stem
                        thumbnail
                        display_name
                        region {
                            name
                        }
                        store {
                            name
                            store_number
                        }
                    }
                    products {
                        product_id
                        image
                        price
                        sale_price
                        title
                        link
                    }
                    attributes {
                        code
                        value
                    }
                    content_type
                    creation_date
                    asset {
                        asset_type
                        video {
                            thumbnails {
                                width
                                height
                                url
                            }
                        }
                        photo {
                            thumbnails {
                                url
                                width
                                height
                            }
                        }
                    }
                }
            }
        }`;

        const response = await axios.post(apiUrl, { query: requestQuery }, { withCredentials: true });
        return response.data.data.content.items.map((item) => ({
            ...item,
            title: item.title,
            user_id: item?.creator?.creator_id,
            description: item.description,
            id: item.content_id,
            category: category,
            entityType: item.content_type === "video" ? 1 : 7,
            owner: item.creator,
        }));
    };

    render() {
        return <ThemeProviderWrapper>
            <Container>
                <Breadcrumbs keepOnMobile/>
            </Container>
            <Container>
                <Box my={2}>
                <Typography variant={'h1'} sx={{color: '#323232'}} align={'center'}>Latest Posts</Typography>
                </Box>
                <Box pb={{xs: 2, sm: 3, md: 4}}>
                    <Typography align={'center'} fontSize={'16px'} lineHeight={'24px'} sx={{color: '#323232'}}>
                        Shop curated collections and discover new ways to mix, match, and sparkle.
                    </Typography>
                </Box>
            </Container>
            <SearchBar updateSearchResults={this.updateRequestParameter} resetRequestParameter={this.resetRequestParameter} />
            {this.state.loaded && (
                <Grid
                    fetchAssets={this.fetchAssets}
                    filters={{
                        category: this.state.category,
                        sort: this.state.sort
                    }}
                    gridItemProps={{
                        maxDescriptionLines: 2,
                        redirect: true,
                        lazyLoadingThumbnails: true
                    }}
                    noResultsText="No results found."
                />
            )}
        </ThemeProviderWrapper>
    }
}

const targetEl = document.getElementById("app-container");
let root;

function isHomeRoute() {
    const subFolder = ConfigurationHelper.getGlobalConfiguration().sub_folder || '';
    let pathname = window.location.pathname;
    // Normalize trailing slash
    if (pathname.length > 1 && pathname.endsWith('/')) pathname = pathname.slice(0, -1);
    let sub = subFolder;
    if (sub.length > 1 && sub.endsWith('/')) sub = sub.slice(0, -1);

    // Home is only when path is exactly subFolder or subFolder + '/'
    const isHome = pathname === sub || pathname === '' || pathname === '/';

    // Allow rendering on localhost or site.<env>.tvpage.com (env = test|stage|app)
    const host = window.location.hostname;
    const envPattern = /^site\.(test|stage|app)\.tvpage\.com$/;
    const isAllowedHost = host === 'localhost' || envPattern.test(host);

    return isHome || isAllowedHost;
}

function cleanupIfNotHomeRoute() {
    if (!isHomeRoute() && root) {
        root.unmount();
        if (targetEl) targetEl.innerHTML = '';
        window.removeEventListener('popstate', cleanupIfNotHomeRoute);
        window.removeEventListener('hashchange', cleanupIfNotHomeRoute);
    }
}

if (targetEl && isHomeRoute()) {
    targetEl.style.backgroundColor = '#f8f8f8';
    root = createRoot(targetEl);
    root.render(<Home/>);
}

// Patch history methods for Angular/SPA navigation
const _pushState = history.pushState;
history.pushState = function() {
    _pushState.apply(this, arguments);
    cleanupIfNotHomeRoute();
};
const _replaceState = history.replaceState;
history.replaceState = function() {
    _replaceState.apply(this, arguments);
    cleanupIfNotHomeRoute();
};

window.addEventListener('popstate', cleanupIfNotHomeRoute);
window.addEventListener('hashchange', cleanupIfNotHomeRoute);
cleanupIfNotHomeRoute();
